import { ReactNode, createContext, useState } from 'react'

import { generateRandomID } from '@/utils/idGenerator'

import { CompanyResponse, CreateBrandRequest, CreateCompanyRequest, UploadFileRequest } from '@shared/models'

import { Routes } from '@/configs'

import { Api } from '@/modules/api'

import { useRouting } from '@/hooks/useRouting'

export type CreateCompanyType = Omit<CreateCompanyRequest, 'brands'>

export type CreateBrandType = Omit<CreateBrandRequest, 'companyID'> & {
  id: string
}

export type AddBrandInput = Omit<CreateBrandType, 'id'>

interface CompanyFormContextOutput {
  addBrand: (input: AddBrandInput) => void
  deleteBrand: (brandID: string) => void
  save: (input: CreateCompanyType) => void

  isCreatingCompany: boolean
  brands: CreateBrandType[]
}

interface ContextProps {
  children: ReactNode
}

export const CompanyFormContext = createContext<CompanyFormContextOutput>({} as CompanyFormContextOutput)

export function CompanyFormContextProvider({ children }: ContextProps) {
  const [brands, setBrands] = useState<CreateBrandType[]>([])
  const { isRunning: isCreatingCompany, run: createCompany } = Api.Command<CompanyResponse>(
    Api.Methods.POST,
    'companies',
  )

  const { navigate } = useRouting()

  function addBrand(addBrandInput: AddBrandInput) {
    setBrands((state) => [
      ...state,
      {
        ...addBrandInput,
        id: generateRandomID(),
      },
    ])
  }

  function deleteBrand(id: string) {
    setBrands((state) => state.filter((brand) => brand.id !== id))
  }

  async function submit(input: CreateCompanyType) {
    const submitInput: CreateCompanyRequest = {
      name: input.name,
      hasCompetitors: input.hasCompetitors,
      isGovernmental: input.isGovernmental,
      motherID: input.motherID,
      programsAndInitiatives: input.programsAndInitiatives,
      advertisingCampaigns: input.advertisingCampaigns,
      slogans: input.slogans,
      nicknamesAndCommonNames: input.nicknamesAndCommonNames,
      products: input.products,
      brands: brands.map((brandInput: CreateBrandType): Omit<CreateBrandRequest, 'companyID'> => {
        const brand: Omit<CreateBrandRequest, 'companyID'> = {
          name: brandInput.name,
          termsString: brandInput.termsString,
        }
        if (brandInput.systemLogo) brand.systemLogo = { ...brandInput.systemLogo } as UploadFileRequest
        if (brandInput.reportLogo) brand.reportLogo = { ...brandInput.reportLogo } as UploadFileRequest
        return brand
      }),
    }

    if (input.systemLogo) submitInput.systemLogo = { ...input.systemLogo } as UploadFileRequest
    if (input.reportLogo) submitInput.reportLogo = { ...input.reportLogo } as UploadFileRequest
    createCompany({
      input: submitInput,
      onSuccess: (company) => navigate(Routes.CompanyDetails, company.id),
    })
  }

  return (
    <CompanyFormContext.Provider
      value={{
        addBrand,
        deleteBrand,
        save: submit,

        isCreatingCompany,
        brands: brands,
      }}>
      {children}
    </CompanyFormContext.Provider>
  )
}
